import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Card,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Button,
  Paper,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Calendar as CalendarIcon,
  FileText,
  Search,
  CreditCard,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';
import { useJob } from '../context/JobContext';
import { useLocation } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const StatWidget = ({ title, value, subtitle, icon: Icon, color, onClick, active }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      borderWidth: active ? 2 : 1,
      borderStyle: 'solid',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
      boxShadow: active
        ? '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)'
        : '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
      transition: 'box-shadow 0.2s, transform 0.1s, border-color 0.2s',
      '&:hover': onClick
        ? {
            boxShadow:
              '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
            transform: 'translateY(-1px)'
          }
        : undefined
    }}
    elevation={0}
    variant="outlined"
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

const isValidEmail = (value) => {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const parseEmailList = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return [];
  return raw
    .split(/[;,]+/)
    .map((p) => String(p || '').trim())
    .filter(Boolean);
};

const isValidEmailList = (value) => {
  const list = parseEmailList(value);
  if (list.length === 0) return false;
  return list.every(isValidEmail);
};

const escapeHtml = (text) => {
  const s = String(text ?? '');
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const textToQuillHtml = (text) => {
  const raw = String(text ?? '').replace(/\r\n/g, '\n');
  const lines = raw.split('\n');
  const html = lines.map((l) => `<p>${escapeHtml(l)}</p>`).join('');
  return html || '<p><br></p>';
};

const isQuillEmpty = (html) => {
  const s = String(html || '').trim();
  if (!s) return true;
  return s === '<p><br></p>' || s === '<p></p>' || s === '<br>' || s === '<br/>' || s === '<br />';
};

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'font',
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'script',
  'align',
  'list',
  'bullet',
  'indent',
  'blockquote',
  'code-block',
  'link',
  'image'
];

export default function PasspFatture() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_documento');
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [azienda, setAzienda] = useState('all');
  const [tipoIncasso, setTipoIncasso] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [tipoIncassoOptions, setTipoIncassoOptions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    serie: false,
    numero: false,
    cod_conto: false,
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    totalAmount: 0,
    averageAmount: 0,
    totalFT: 0,
    totalNC: 0,
    amountFT: 0,
    amountNC: 0,
  });
  const [docType, setDocType] = useState('all'); // 'all' | 'FT' | 'NC'
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncAzienda, setSyncAzienda] = useState('');
  const [syncMode, setSyncMode] = useState('incremental');
  const [syncRebuild, setSyncRebuild] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [syncLatestDate, setSyncLatestDate] = useState(null);
  const [loadingLatestDate, setLoadingLatestDate] = useState(false);
  const { addJob, jobs, addMonitoringNotification } = useJob();
  const activeRequestIdRef = useRef(0);
  const activeStatsRequestIdRef = useRef(0);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDialogRow, setPrintDialogRow] = useState(null);
  const [printSendEmail, setPrintSendEmail] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [checkingUniqueCustomer, setCheckingUniqueCustomer] = useState(false);
  const [uniqueCustomer, setUniqueCustomer] = useState(null);
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false);
  const [bulkEmailAccountId, setBulkEmailAccountId] = useState('');
  const [bulkEmailStarting, setBulkEmailStarting] = useState(false);
  const [bulkEmailForm, setBulkEmailForm] = useState({
    to: '',
    subject: '',
    body: '<p><br></p>',
  });
  const location = useLocation();

  const baseColumnsCount = 8;
  const extraColumnsCount =
    (columnVisibility.id ? 1 : 0) +
    (columnVisibility.serie ? 1 : 0) +
    (columnVisibility.numero ? 1 : 0) +
    (columnVisibility.cod_conto ? 1 : 0);
  const totalColumns = baseColumnsCount + extraColumnsCount;

  const formatCurrency = (value) => {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleFilterAzienda = (event) => {
    const value = event.target.value;
    setAzienda(value);
    setPage(0);
    fetchStats({ azienda: value });
  };

  const handleFilterTipoIncasso = (event) => {
    const value = event.target.value;
    setTipoIncasso(value);
    setPage(0);
    fetchStats({ tipoIncasso: value });
  };

  const handleFilterDateFrom = (event) => {
    const value = event.target.value;
    setDateFrom(value);
    setPage(0);
    fetchStats({ dateFrom: value });
  };

  const handleFilterDateTo = (event) => {
    const value = event.target.value;
    setDateTo(value);
    setPage(0);
    fetchStats({ dateTo: value });
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleOpenPrintDialog = (row) => {
    setPrintDialogRow(row);
    setPrintSendEmail(false);
    setSelectedEmailAccountId('');
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    if (printing) return;
    setPrintDialogOpen(false);
    setPrintDialogRow(null);
    setPrintSendEmail(false);
  };

  const handleConfirmPrint = async () => {
    if (!printDialogRow) return;
    if (printSendEmail && !selectedEmailAccountId) {
      toast.error('Seleziona un account email per inviare la copia al cliente');
      return;
    }
    setPrinting(true);
    try {
      const res = await api.post(
        `/passp-fatture/${printDialogRow.id}/print`,
        { sendEmail: printSendEmail, emailAccountId: selectedEmailAccountId || null },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Documento generato con successo');
      addMonitoringNotification({
        kind: 'passp_fatture_print',
        docId: printDialogRow.id,
        sigla: printDialogRow.sigla,
        serie: printDialogRow.serie,
        numero: printDialogRow.numero,
        serie_numero: printDialogRow.serie_numero,
        azienda: printDialogRow.azienda,
        cliente: printDialogRow.cliente_ragione_sociale,
        email: printDialogRow.cliente_email,
        totale: printDialogRow.totale_documento,
        data_documento: printDialogRow.data_documento,
      });
      setPrintDialogOpen(false);
    } catch (error) {
      console.error('Error printing Passpartout document:', error);
      toast.error('Errore durante la stampa del documento');
    } finally {
      setPrinting(false);
    }
  };

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const fetchFilters = async () => {
    try {
      const res = await api.get('/passp-fatture/filters/options');
      const data = res.data || {};
      setAziendeOptions((data.aziende || []).filter(Boolean));
      setTipoIncassoOptions((data.tipiIncasso || []).filter(Boolean));
    } catch (error) {
      console.error('Error fetching Passpartout invoices filters:', error);
      toast.error('Errore nel caricamento dei filtri Fatture Passpartout');
    }
  };

  const fetchEmailAccounts = async () => {
    try {
      const res = await api.get('/email-accounts', {
        params: { is_active: 1, limit: 100 },
      });
      const data = res.data || {};
      setEmailAccounts(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error('Error fetching email accounts:', error);
    }
  };

  const buildFiltersPayload = () => ({
    search: searchValue || '',
    azienda: azienda !== 'all' ? azienda : null,
    tipoIncasso: tipoIncasso !== 'all' ? tipoIncasso : null,
    dateFrom: dateFrom || null,
    dateTo: dateTo || null,
    sigla: docType !== 'all' ? docType : null,
  });

  const selectedCount = useMemo(() => {
    if (selectAllFiltered) {
      const total = Number(totalItems) || 0;
      return Math.max(0, total - excludedIds.size);
    }
    return selectedIds.size;
  }, [selectAllFiltered, totalItems, excludedIds, selectedIds]);

  const clearSelection = () => {
    setSelectAllFiltered(false);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const toggleRowSelection = (rowId) => {
    const id = Number(rowId);
    if (!Number.isFinite(id)) return;

    if (selectAllFiltered) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAllFiltered = (checked) => {
    setSelectAllFiltered(Boolean(checked));
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const handleOpenBulkEmailDialog = () => {
    if (bulkEmailStarting) return;
    const defaultTo = uniqueCustomer?.cliente_email || '';
    const customerLabel =
      uniqueCustomer?.cliente_ragione_sociale || uniqueCustomer?.cod_conto || 'cliente';
    const defaultSubject =
      selectedCount > 0
        ? `Invio documenti Passpartout - ${customerLabel} (${selectedCount} allegati)`
        : `Invio documenti Passpartout - ${customerLabel}`;
    const defaultBodyText = `Gentile ${customerLabel},\n\nin allegato trasmettiamo i documenti Passpartout richiesti.\n\nRestiamo a disposizione per eventuali chiarimenti.\n\nCordiali saluti\nDebitoBancario`;
    const defaultBody = textToQuillHtml(defaultBodyText);

    const firstAccountId =
      Array.isArray(emailAccounts) && emailAccounts.length > 0
        ? String(emailAccounts[0]?.id || '')
        : '';

    setBulkEmailAccountId(firstAccountId);
    setBulkEmailForm({
      to: defaultTo,
      subject: defaultSubject,
      body: defaultBody,
    });
    setBulkEmailDialogOpen(true);
  };

  const handleCloseBulkEmailDialog = () => {
    if (bulkEmailStarting) return;
    setBulkEmailDialogOpen(false);
    setBulkEmailAccountId('');
    setBulkEmailForm({
      to: '',
      subject: '',
      body: '<p><br></p>',
    });
  };

  const handleConfirmBulkEmail = async () => {
    if (bulkEmailStarting) return;
    if (!uniqueCustomer?.unique) {
      toast.error('Il filtro deve riguardare un solo cliente');
      return;
    }
    if (!bulkEmailAccountId) {
      toast.error('Seleziona un account email');
      return;
    }
    if (!bulkEmailForm.to || !isValidEmailList(bulkEmailForm.to)) {
      toast.error('Inserisci un indirizzo email valido (puoi usare più destinatari separati da ; o ,)');
      return;
    }
    if (selectedCount <= 0) {
      toast.error('Seleziona almeno una fattura');
      return;
    }

    setBulkEmailStarting(true);
    try {
      const res = await api.post('/passp-fatture/bulk/send-email', {
        emailAccountId: bulkEmailAccountId,
        emailTo: bulkEmailForm.to,
        emailSubject: bulkEmailForm.subject,
        emailBodyHtml: isQuillEmpty(bulkEmailForm.body) ? '' : bulkEmailForm.body,
        selectAllFiltered,
        excludedIds: Array.from(excludedIds),
        ids: Array.from(selectedIds),
        filters: buildFiltersPayload(),
      });

      const jobId = res.data?.jobId;
      if (!jobId) {
        toast.error('Impossibile avviare l’invio email');
        return;
      }

      addJob(jobId, 'passp_fatture_bulk_send_email', {
        meta: {
          to: bulkEmailForm.to,
          count: selectedCount,
          cliente: uniqueCustomer.cliente_ragione_sociale,
        },
      });

      handleCloseBulkEmailDialog();
      clearSelection();
    } catch (error) {
      console.error('Error starting bulk email:', error);
      toast.error('Errore durante l’avvio invio email');
    } finally {
      setBulkEmailStarting(false);
    }
  };

  const fetchData = async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (searchValue) params.search = searchValue;
      if (azienda !== 'all') params.azienda = azienda;
      if (tipoIncasso !== 'all') params.tipoIncasso = tipoIncasso;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (docType !== 'all') params.sigla = docType;

      const res = await api.get('/passp-fatture', { params });
      if (activeRequestIdRef.current !== requestId) {
        return;
      }
      const data = res.data || {};
      setRows(Array.isArray(data.data) ? data.data : []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Error fetching Passpartout invoices:', error);
      if (error.response) {
        toast.error('Errore nel caricamento Fatture Passpartout');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (overrides = {}) => {
    const requestId = activeStatsRequestIdRef.current + 1;
    activeStatsRequestIdRef.current = requestId;
    try {
      const nextSearchValue =
        overrides.searchValue !== undefined ? overrides.searchValue : searchValue;
      const nextAzienda = overrides.azienda !== undefined ? overrides.azienda : azienda;
      const nextTipoIncasso =
        overrides.tipoIncasso !== undefined ? overrides.tipoIncasso : tipoIncasso;
      const nextDateFrom = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const nextDateTo = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
      const nextDocType = overrides.docType !== undefined ? overrides.docType : docType;
      const params = {};
      if (nextSearchValue) params.search = nextSearchValue;
      if (nextAzienda !== 'all') params.azienda = nextAzienda;
      if (nextTipoIncasso !== 'all') params.tipoIncasso = nextTipoIncasso;
      if (nextDateFrom) params.dateFrom = nextDateFrom;
      if (nextDateTo) params.dateTo = nextDateTo;
      if (nextDocType !== 'all') params.sigla = nextDocType;

      const res = await api.get('/passp-fatture/stats', { params });
      if (activeStatsRequestIdRef.current !== requestId) {
        return;
      }
      const data = res.data || {};
      setSummary({
        total: data.total || 0,
        totalAmount: data.totalAmount || 0,
        averageAmount: data.averageAmount || 0,
        totalFT: data.totalFT || 0,
        totalNC: data.totalNC || 0,
        amountFT: data.amountFT || 0,
        amountNC: data.amountNC || 0,
      });
    } catch (error) {
      console.error('Error fetching Passpartout invoices stats:', error);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchEmailAccounts();
  }, []);

  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const aziendaParam = params.get('azienda');
    const clienteCodice = params.get('clienteCodice');
    const siglaParam = params.get('sigla');
    const numeroParam = params.get('numero');
    if (aziendaParam) {
      setAzienda(aziendaParam);
    }
    if (clienteCodice) {
      setSearchInput(clienteCodice);
      setSearchValue(clienteCodice);
      setPage(0);
    }
    if (siglaParam) {
      setDocType(siglaParam);
    }
    if (numeroParam) {
      setSearchInput(numeroParam);
      setSearchValue(numeroParam);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, order, orderBy, searchValue, azienda, tipoIncasso, dateFrom, dateTo, docType]);

  useEffect(() => {
    fetchStats();
  }, [searchValue, azienda, tipoIncasso, dateFrom, dateTo, docType]);

  useEffect(() => {
    clearSelection();
    setUniqueCustomer(null);
  }, [searchValue, azienda, tipoIncasso, dateFrom, dateTo, docType]);

  useEffect(() => {
    const requestId = Date.now();
    let active = true;
    const fetchUnique = async () => {
      setCheckingUniqueCustomer(true);
      try {
        const payload = buildFiltersPayload();
        const params = {};
        if (payload.search) params.search = payload.search;
        if (payload.azienda) params.azienda = payload.azienda;
        if (payload.tipoIncasso) params.tipoIncasso = payload.tipoIncasso;
        if (payload.dateFrom) params.dateFrom = payload.dateFrom;
        if (payload.dateTo) params.dateTo = payload.dateTo;
        if (payload.sigla) params.sigla = payload.sigla;
        const res = await api.get('/passp-fatture/filters/unique-customer', {
          params: { ...params, _: requestId },
        });
        if (!active) return;
        const data = res.data || null;
        setUniqueCustomer(data && typeof data === 'object' ? data : null);
      } catch (error) {
        if (!active) return;
        setUniqueCustomer(null);
      } finally {
        if (active) setCheckingUniqueCustomer(false);
      }
    };

    fetchUnique();
    return () => {
      active = false;
    };
  }, [searchValue, azienda, tipoIncasso, dateFrom, dateTo, docType]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      setPage(0);
      if (trimmed.length >= 3) {
        setSearchValue(trimmed);
        fetchStats({ searchValue: trimmed });
      } else {
        setSearchValue('');
        fetchStats({ searchValue: '' });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const syncJobs = jobs.filter(
      (j) => j.type === 'passp_fatture_sync' && j.status === 'completed'
    );
    if (syncJobs.length > 0) {
      fetchData();
      fetchStats();
    }
  }, [jobs]);

  const empty = !loading && rows.length === 0;
  const isUniqueCustomer = Boolean(uniqueCustomer?.unique);
  const canBulkEmail =
    isUniqueCustomer &&
    selectedCount > 0 &&
    !bulkEmailStarting;

  const bulkEmailDisabledReason = useMemo(() => {
    if (bulkEmailStarting) return 'Operazione in corso';
    if (!isUniqueCustomer) return 'Disponibile solo quando il filtro riguarda un solo cliente';
    if (selectedCount <= 0) return 'Seleziona almeno una fattura';
    return '';
  }, [bulkEmailStarting, isUniqueCustomer, uniqueCustomer, selectedCount]);

  const averageLabel = useMemo(() => {
    if (!summary.total || !summary.averageAmount) return '-';
    return formatCurrency(summary.averageAmount);
  }, [summary]);

  const ftPct = useMemo(() => {
    if (!summary.total || !summary.totalFT) return '0%';
    const value = (summary.totalFT * 100) / summary.total;
    return `${value.toFixed(1)}%`;
  }, [summary]);

  const ncPct = useMemo(() => {
    if (!summary.total || !summary.totalNC) return '0%';
    const value = (summary.totalNC * 100) / summary.total;
    return `${value.toFixed(1)}%`;
  }, [summary]);

  const handleWidgetFilterAll = () => {
    setDocType('all');
    setPage(0);
    fetchStats({ docType: 'all' });
  };

  const handleWidgetFilterFT = () => {
    setDocType('FT');
    setPage(0);
    fetchStats({ docType: 'FT' });
  };

  const handleWidgetFilterNC = () => {
    setDocType('NC');
    setPage(0);
    fetchStats({ docType: 'NC' });
  };

  const formatDisplayDate = (value) => {
    if (!value || typeof value !== 'string') return value;
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const [yyyy, mm, dd] = parts;
    return `${dd}/${mm}/${yyyy}`;
  };

  const getTodayYmd = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
      today.getDate()
    ).padStart(2, '0')}`;
  };

  const activeFilters = useMemo(() => {
    const items = [];
    if (searchValue) {
      items.push({ key: 'search', label: `Ricerca: ${searchValue}` });
    }
    if (docType !== 'all') {
      items.push({ key: 'docType', label: `Documento: ${docType}` });
    }
    if (azienda !== 'all') {
      const found = aziendeOptions.find((item) => item.code === azienda);
      items.push({ key: 'azienda', label: `Azienda: ${found?.name || azienda}` });
    }
    if (tipoIncasso !== 'all') {
      items.push({ key: 'tipoIncasso', label: `Tipo incasso: ${tipoIncasso}` });
    }
    if (dateFrom) {
      items.push({ key: 'dateFrom', label: `Da: ${formatDisplayDate(dateFrom)}` });
    }
    if (dateTo) {
      items.push({ key: 'dateTo', label: `A: ${formatDisplayDate(dateTo)}` });
    }
    return items;
  }, [searchValue, docType, azienda, aziendeOptions, tipoIncasso, dateFrom, dateTo]);

  const clearActiveFilter = (key) => {
    if (key === 'search') {
      setSearchInput('');
      setSearchValue('');
      setPage(0);
      fetchStats({ searchValue: '' });
      return;
    }
    if (key === 'docType') {
      setDocType('all');
      setPage(0);
      fetchStats({ docType: 'all' });
      return;
    }
    if (key === 'azienda') {
      setAzienda('all');
      setPage(0);
      fetchStats({ azienda: 'all' });
      return;
    }
    if (key === 'tipoIncasso') {
      setTipoIncasso('all');
      setPage(0);
      fetchStats({ tipoIncasso: 'all' });
      return;
    }
    if (key === 'dateFrom') {
      setDateFrom('');
      setPage(0);
      fetchStats({ dateFrom: '' });
      return;
    }
    if (key === 'dateTo') {
      setDateTo('');
      setPage(0);
      fetchStats({ dateTo: '' });
    }
  };

  const handleSync = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const firstDay = '01';

    setSyncMode('incremental');
    setSyncRebuild(false);
    setSyncLatestDate(null);
    setSyncDateFrom(`${year}-${month}-${firstDay}`);
    setSyncDateTo(`${year}-${month}-${day}`);
    if (!syncAzienda && aziendeOptions.length === 1) {
      setSyncAzienda(aziendeOptions[0].code);
    }
    setSyncDialogOpen(true);
  };

  const handleConfirmSync = async () => {
    if (!syncAzienda) {
      toast.error('Seleziona l’azienda per la sincronizzazione');
      return;
    }
    if (syncMode === 'range' && (!syncDateFrom || !syncDateTo)) {
      toast.error('Compila data inizio e data fine per la sincronizzazione a intervallo');
      return;
    }

    setSyncing(true);
    const pendingToastId = toast.loading('Avvio sincronizzazione Fatture Passpartout...');
    try {
      const incrementalFrom = syncLatestDate || getTodayYmd();
      const incrementalTo = getTodayYmd();
      const res = await api.post('/passp-fatture/sync', {
        azienda: syncAzienda,
        mode: syncMode,
        date_from: syncMode === 'range' ? syncDateFrom : incrementalFrom,
        date_to: syncMode === 'range' ? syncDateTo : incrementalTo,
        rebuild: syncMode === 'range' ? syncRebuild : false,
      });
      const jobId = res.data?.jobId;
      const startMessage = res.data?.message || 'Sincronizzazione Fatture Passpartout avviata';
      if (jobId) {
        addJob(jobId, 'passp_fatture_sync', { silent: true });
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      } else {
        toast.update(pendingToastId, {
          render: startMessage,
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        });
      }
      setSyncDialogOpen(false);
    } catch (error) {
      console.error('Passp Fatture sync error:', error);
      toast.update(pendingToastId, {
        render: 'Errore durante la sincronizzazione Fatture Passpartout',
        type: 'error',
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const fetchLatestDate = async () => {
      if (!syncDialogOpen || !syncAzienda || syncMode !== 'incremental') {
        return;
      }
      setLoadingLatestDate(true);
      try {
        const res = await api.get('/passp-fatture/sync/latest-date', {
          params: { azienda: syncAzienda },
        });
        setSyncLatestDate(res.data?.latestDate || null);
      } catch (error) {
        setSyncLatestDate(null);
        toast.error('Errore nel recupero ultima data documento');
      } finally {
        setLoadingLatestDate(false);
      }
    };
    fetchLatestDate();
  }, [syncDialogOpen, syncAzienda, syncMode]);

  useEffect(() => {
    if (syncMode === 'incremental' && syncRebuild) {
      setSyncRebuild(false);
    }
  }, [syncMode, syncRebuild]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
            }}
          >
            <FileText size={22} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Fatture Passpartout
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Movimenti di magazzino sincronizzati dal gestionale Passpartout
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={bulkEmailDisabledReason || 'Invia email con PDF allegati'}>
            <span>
              <Button
                variant="outlined"
                onClick={handleOpenBulkEmailDialog}
                disabled={!canBulkEmail}
                startIcon={<EmailIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  px: 2.5,
                  py: 0.75,
                }}
              >
                Invia email
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            onClick={handleSync}
            disabled={syncing}
            startIcon={
              syncing ? (
                <LoaderProgress size={20} intervalMs={80} step={15} maxBeforeDone={90} />
              ) : (
                <RefreshCw size={18} />
              )
            }
            sx={{
              textTransform: 'none',
              px: 2.5,
              py: 0.75,
            }}
          >
            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
          </Button>
        </Box>
      </Box>

      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                color: '#16A34A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertTriangle size={22} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sincronizza
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sei sicuro di volerlo fare?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={syncAzienda}
                label="Azienda"
                onChange={(e) => setSyncAzienda(e.target.value)}
              >
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {syncAzienda ? (
              <>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={syncMode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSyncMode(value);
                      if (value === 'incremental') {
                        setSyncRebuild(false);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="incremental"
                      control={<Radio />}
                      label="Incrementale (da ultimo documento)"
                    />
                    <FormControlLabel
                      value="range"
                      control={<Radio />}
                      label="Intervallo date personalizzato"
                    />
                  </RadioGroup>
                </FormControl>

                {syncMode === 'incremental' ? (
                  <Alert severity="info" sx={{ alignItems: 'center' }}>
                    {loadingLatestDate ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="body2">
                          Recupero ultima data documento...
                        </Typography>
                      </Box>
                    ) : syncLatestDate ? (
                      `Verrà sincronizzato in modalità incrementale dal ${formatDisplayDate(
                        syncLatestDate
                      )} a oggi.`
                    ) : (
                      'Nessun documento trovato per questa azienda: la sincronizzazione incrementale partirà da oggi.'
                    )}
                  </Alert>
                ) : (
                  <>
                    <TextField
                      label="Data inizio"
                      type="date"
                      size="small"
                      value={syncDateFrom}
                      onChange={(e) => setSyncDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Data fine"
                      type="date"
                      size="small"
                      value={syncDateTo}
                      onChange={(e) => setSyncDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={syncRebuild}
                            onChange={(e) => setSyncRebuild(e.target.checked)}
                          />
                        }
                        label="Riallinea (cancella e risincronizza)"
                      />
                    </FormGroup>
                    {syncRebuild ? (
                      <Alert severity="warning">
                        Verranno cancellati i documenti già importati nel periodo selezionato e poi
                        risincronizzati da Passpartout.
                      </Alert>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleConfirmSync} variant="contained" disabled={syncing}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Fatture filtrate"
            value={summary.total || 0}
            icon={FileText}
            color="#2563EB"
            active={docType === 'all'}
            onClick={handleWidgetFilterAll}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Valore totale"
            value={formatCurrency(summary.totalAmount)}
            icon={CreditCard}
            color="#16A34A"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Fatture (FT)"
            value={formatCurrency(summary.amountFT)}
            subtitle={`N. ${summary.totalFT || 0} • Quota ${ftPct}`}
            icon={TrendingUp}
            color="#CA8A04"
            active={docType === 'FT'}
            onClick={handleWidgetFilterFT}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Note di credito (NC)"
            value={formatCurrency(summary.amountNC)}
            subtitle={`N. ${summary.totalNC || 0} • Quota ${ncPct}`}
            icon={AlertTriangle}
            color="#DC2626"
            active={docType === 'NC'}
            onClick={handleWidgetFilterNC}
          />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca per sigla, numero, cliente..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Azienda</InputLabel>
              <Select
                value={azienda}
                label="Azienda"
                onChange={handleFilterAzienda}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {aziendeOptions.map((item) => (
                  <MenuItem key={item.code} value={item.code}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton size="small" onClick={handleToggleFilters}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Colonne">
              <IconButton size="small" onClick={handleOpenColumnMenu}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {activeFilters.length > 0 ? (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 0.5 }}>
              Filtri attivi
            </Typography>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                size="small"
                onDelete={() => clearActiveFilter(filter.key)}
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.24)'
                      : '#F3F4F6',
                  color: theme.palette.text.primary,
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'rgba(148, 163, 184, 0.35)',
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiChip-deleteIcon:hover': {
                    color: theme.palette.text.primary,
                  },
                })}
              />
            ))}
          </Box>
        ) : null}

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tipo incasso</InputLabel>
              <Select
                value={tipoIncasso}
                label="Tipo incasso"
                onChange={handleFilterTipoIncasso}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {tipoIncassoOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Data da"
              type="date"
              size="small"
              value={dateFrom}
              onChange={handleFilterDateFrom}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon size={16} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Data a"
              type="date"
              size="small"
              value={dateTo}
              onChange={handleFilterDateTo}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon size={16} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.id}
                  onChange={() => handleToggleColumn('id')}
                />
              }
              label="ID"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.serie}
                  onChange={() => handleToggleColumn('serie')}
                />
              }
              label="Serie"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.numero}
                  onChange={() => handleToggleColumn('numero')}
                />
              }
              label="Numero"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.cod_conto}
                  onChange={() => handleToggleColumn('cod_conto')}
                />
              }
              label="Codice conto"
            />
          </FormGroup>
        </Menu>

        <Box
          sx={{
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={selectAllFiltered}
                  indeterminate={!selectAllFiltered && selectedIds.size > 0}
                  onChange={(e) => handleToggleSelectAllFiltered(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Seleziona tutti i risultati filtrati ({totalItems || 0})
                </Typography>
              }
            />

            {checkingUniqueCustomer ? (
              <Typography variant="caption" color="text.secondary">
                Controllo cliente...
              </Typography>
            ) : isUniqueCustomer ? (
              <Chip
                size="small"
                color="success"
                label={`Cliente: ${uniqueCustomer?.cliente_ragione_sociale || uniqueCustomer?.cod_conto || ''}`}
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <Chip
                size="small"
                label="Filtro multi-cliente"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              color={selectedCount > 0 ? 'success' : 'default'}
              label={`Selezionate: ${selectedCount}`}
              sx={{ fontWeight: 700 }}
            />
            <Tooltip title="Pulisci selezione">
              <span>
                <IconButton
                  size="small"
                  onClick={clearSelection}
                  disabled={selectedCount <= 0}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell padding="checkbox" sx={{ width: 96 }}>
                  <Checkbox
                    size="small"
                    checked={selectAllFiltered && excludedIds.size === 0 && Number(totalItems) > 0}
                    indeterminate={
                      selectAllFiltered
                        ? excludedIds.size > 0
                        : selectedIds.size > 0
                    }
                    onChange={(e) => {
                      if (e.target.checked) handleToggleSelectAllFiltered(true);
                      else clearSelection();
                    }}
                  />
                </TableCell>
                {columnVisibility.id && (
                  <TableCell sx={{ width: 80 }}>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_documento'}
                    direction={
                      orderBy === 'data_documento' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('data_documento')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'azienda'}
                    direction={orderBy === 'azienda' ? order : 'asc'}
                    onClick={() => handleRequestSort('azienda')}
                  >
                    Azienda
                  </TableSortLabel>
                </TableCell>
                <TableCell>Sigla</TableCell>
                {columnVisibility.serie && <TableCell>Serie</TableCell>}
                {columnVisibility.numero && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'numero'}
                      direction={orderBy === 'numero' ? order : 'asc'}
                      onClick={() => handleRequestSort('numero')}
                    >
                      Numero
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'numero'}
                    direction={orderBy === 'numero' ? order : 'asc'}
                    onClick={() => handleRequestSort('numero')}
                  >
                    Numero
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.cod_conto && (
                  <TableCell>Codice conto</TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cliente'}
                    direction={orderBy === 'cliente' ? order : 'asc'}
                    onClick={() => handleRequestSort('cliente')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'totale_documento'}
                    direction={
                      orderBy === 'totale_documento' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('totale_documento')}
                  >
                    Importo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Tipo incasso</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <LoaderProgress />
                  </TableCell>
                </TableRow>
              )}
              {empty && !loading && (
                <TableRow>
                  <TableCell colSpan={totalColumns}>
                    <Box
                      sx={{
                        py: 6,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Nessun documento trovato
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Modifica i filtri o la ricerca per visualizzare le
                        fatture disponibili.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => {
                  const rowId = Number(row.id);
                  const rowSelected = selectAllFiltered
                    ? !excludedIds.has(rowId)
                    : selectedIds.has(rowId);

                  return (
                    <TableRow key={row.id} hover>
                    <TableCell padding="checkbox">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={rowSelected}
                          onChange={() => toggleRowSelection(rowId)}
                        />
                        <Tooltip title="Stampa documento (PDF)">
                          <IconButton
                            size="small"
                            sx={{
                              color: 'error.main',
                              bgcolor: 'rgba(239,68,68,0.06)',
                              '&:hover': {
                                bgcolor: 'rgba(239,68,68,0.16)',
                              },
                            }}
                            onClick={() => handleOpenPrintDialog(row)}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {columnVisibility.id && <TableCell>{row.id}</TableCell>}
                    <TableCell>
                      {row.data_documento
                        ? new Date(row.data_documento).toLocaleDateString(
                            'it-IT'
                          )
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.azienda}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={row.sigla} size="small" />
                    </TableCell>
                    {columnVisibility.serie && (
                      <TableCell>{row.serie || '-'}</TableCell>
                    )}
                    {columnVisibility.numero && (
                      <TableCell>{row.numero || '-'}</TableCell>
                    )}
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.serie_numero || '-'}
                      </Typography>
                    </TableCell>
                    {columnVisibility.cod_conto && (
                      <TableCell>{row.cod_conto || '-'}</TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.cliente_ragione_sociale || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cod. {row.cliente_codice || 'N/D'} • CF/P.IVA:{' '}
                          {row.cliente_partita_iva ||
                            row.cliente_codice_fiscale ||
                            'N/D'}
                        </Typography>
                        {(row.cliente_telefono || row.cliente_email) && (
                          <Typography variant="caption" color="text.secondary">
                            {row.cliente_telefono || ''}{' '}
                            {row.cliente_email
                              ? `• ${row.cliente_email}`
                              : ''}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            (row.totale_documento || 0) < 0
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {formatCurrency(row.totale_documento)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.tipo_incasso || 'n/d'}
                        size="small"
                        color={
                          row.tipo_incasso === 'contratto/upsell'
                            ? 'success'
                            : row.tipo_incasso === 'transazione'
                            ? 'info'
                            : row.tipo_incasso === 'urgenze'
                            ? 'warning'
                            : 'default'
                        }
                        sx={{ textTransform: 'none' }}
                      />
                    </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={bulkEmailDialogOpen}
        onClose={handleCloseBulkEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: 'rgba(37, 99, 235, 0.14)',
                color: '#2563EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EmailIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Invia email (massivo)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Se i PDF sono molti/pesanti, l’invio verrà suddiviso in più email allo stesso cliente.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 0.5, color: 'text.secondary' }}
            >
              DESTINATARIO
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                rowGap: 1,
                columnGap: 3,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body2">
                {uniqueCustomer?.cliente_ragione_sociale || uniqueCustomer?.cod_conto || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Email cliente
              </Typography>
              <Typography variant="body2">
                {uniqueCustomer?.cliente_email || 'N/D'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Documenti
              </Typography>
              <Typography variant="body2">
                {selectedCount}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={emailAccounts}
              value={
                emailAccounts.find((acc) => String(acc.id) === String(bulkEmailAccountId)) ||
                null
              }
              onChange={(_e, newValue) => {
                setBulkEmailAccountId(newValue ? String(newValue.id) : '');
              }}
              getOptionLabel={(option) =>
                option ? String(option.name || option.email || '') : ''
              }
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name || option.email}
                    </Typography>
                    {option.email ? (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account email"
                  size="small"
                  placeholder="Cerca account..."
                />
              )}
            />

            <TextField
              label="A"
              size="small"
              value={bulkEmailForm.to}
              onChange={(e) =>
                setBulkEmailForm((prev) => ({ ...prev, to: e.target.value }))
              }
              helperText="Puoi inserire più destinatari separati da ; o ,"
              error={Boolean(bulkEmailForm.to) && !isValidEmailList(bulkEmailForm.to)}
            />

            <TextField
              label="Oggetto"
              size="small"
              value={bulkEmailForm.subject}
              onChange={(e) =>
                setBulkEmailForm((prev) => ({ ...prev, subject: e.target.value }))
              }
            />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Corpo email
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={bulkEmailForm.body}
                  onChange={(value) =>
                    setBulkEmailForm((prev) => ({ ...prev, body: value }))
                  }
                  modules={quillModules}
                  formats={quillFormats}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkEmailDialog} disabled={bulkEmailStarting}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmBulkEmail}
            variant="contained"
            disabled={
              bulkEmailStarting ||
              !bulkEmailAccountId ||
              !isUniqueCustomer ||
              !bulkEmailForm.to ||
              !isValidEmailList(bulkEmailForm.to) ||
              selectedCount <= 0
            }
            sx={{ textTransform: 'none' }}
          >
            {bulkEmailStarting ? 'Avvio...' : 'Conferma invio'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={printDialogOpen}
        onClose={handleClosePrintDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                bgcolor: 'rgba(234, 179, 8, 0.14)',
                color: '#CA8A04',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertTriangle size={22} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Stampa documento
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verifica i dati del documento e conferma la stampa in PDF.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {printDialogRow && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 0.5, color: 'text.secondary' }}
              >
                DETTAGLI DOCUMENTO
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  rowGap: 1,
                  columnGap: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Data documento
                </Typography>
                <Typography variant="body2">
                  {printDialogRow.data_documento
                    ? new Date(printDialogRow.data_documento).toLocaleDateString(
                        'it-IT'
                      )
                    : '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Numero
                </Typography>
                <Typography variant="body2">
                  {printDialogRow.serie_numero || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Azienda
                </Typography>
                <Typography variant="body2">
                  {printDialogRow.azienda || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body2">
                  {printDialogRow.cliente_ragione_sociale || '-'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Email cliente
                </Typography>
                <Typography variant="body2">
                  {printDialogRow.cliente_email || 'N/D'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Importo documento
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(printDialogRow.totale_documento || 0)}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Invio copia via email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Puoi inviare una copia di cortesia del documento al cliente, utilizzando uno
              degli account email configurati.
            </Typography>

            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={printSendEmail}
                  onChange={(e) => setPrintSendEmail(e.target.checked)}
                  color="warning"
                  disabled={!printDialogRow?.cliente_email}
                />
              }
              label="Inviare copia di cortesia al cliente via email"
            />

            {printDialogRow && !printDialogRow.cliente_email && (
              <Typography variant="caption" color="text.secondary">
                Nessun indirizzo email cliente disponibile: non è possibile inviare la
                copia di cortesia.
              </Typography>
            )}

            {printSendEmail && (
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Account email</InputLabel>
                <Select
                  value={selectedEmailAccountId}
                  label="Account email"
                  onChange={(e) => setSelectedEmailAccountId(e.target.value)}
                >
                  {emailAccounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {(acc.name || acc.email) + (acc.email ? ` <${acc.email}>` : '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog} disabled={printing}>
            Annulla
          </Button>
          <Button
            onClick={handleConfirmPrint}
            variant="contained"
            color="error"
            disabled={printing}
          >
            {printing ? 'Elaborazione...' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
